package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import songwritersnotebook.app.data.*
import songwritersnotebook.app.database.SongRepository
import songwritersnotebook.app.billing.SubscriptionTier
import songwritersnotebook.app.utils.AudioExporter
import songwritersnotebook.app.utils.AppLogger
import songwritersnotebook.app.utils.TabTextFormatter
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.Dispatchers
import songwritersnotebook.app.AppDispatchers
import kotlinx.datetime.Clock
import songwritersnotebook.app.di.PlatformDependencyProvider
import com.benasher44.uuid.uuid4

enum class SongTab { MY_SONGS, SHARED }

data class SongToTextState(
    val includeSections: Boolean = true,
    val includeChordLabels: Boolean = true,
    val includePianoInfo: Boolean = false,
    val includeGuitarInfo: Boolean = false,
    val includeTabs: Boolean = false,
    val includeNotes: Boolean = true
)

data class HomeUiState(
    val songs: List<Song> = emptyList(),
    val searchQuery: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val songToDelete: Song? = null,
    val songForTextExport: Song? = null,
    val songToTextState: SongToTextState = SongToTextState(),
    val showUpgradeDialog: Boolean = false,
    val upgradeMessage: String? = null,
    val songForAudioExport: Song? = null,
    val isExporting: Boolean = false,
    val shareDialogSong: Song? = null,
    val shareError: String? = null,
    val isSharingLoading: Boolean = false,
    val isRespondingToInvitation: Boolean = false,
    val selectedTab: SongTab = SongTab.MY_SONGS,
    val currentUserId: String? = null,
    val deletedSongIds: Set<String> = emptySet()
)

class HomeViewModel : ViewModel() {
    private val songRepository = PlatformDependencyProvider().getSongRepository()
    private val audioExporter = AudioExporter()
    private val preferencesManager = songwritersnotebook.app.utils.PreferencesManager()
    
    private val _uiState = MutableStateFlow(HomeUiState())
    val uiState: StateFlow<HomeUiState> = _uiState.asStateFlow()

    private val _dailyReminderEnabled = MutableStateFlow(preferencesManager.dailyReminderEnabled)
    val dailyReminderEnabled: StateFlow<Boolean> = _dailyReminderEnabled.asStateFlow()

    init {
        loadSongs()
    }
    
    fun clearError() {
        _uiState.update { it.copy(error = null, shareError = null) }
    }
    
    fun toggleDailyReminder(enabled: Boolean? = null) {
        val newValue = enabled ?: !preferencesManager.dailyReminderEnabled
        preferencesManager.dailyReminderEnabled = newValue
        _dailyReminderEnabled.value = newValue
        
        val notificationManager = songwritersnotebook.app.utils.NotificationManager()
        if (newValue) {
            notificationManager.scheduleDailyReminder()
        } else {
            notificationManager.cancelDailyReminder()
        }
    }

    val invitations: StateFlow<List<Invitation>> = songRepository?.invitations ?: MutableStateFlow(emptyList())

    val filteredSongs: StateFlow<List<Song>> = combine(
        _uiState,
        songRepository?.songs ?: flowOf(emptyList()),
        songRepository?.sharedSongs ?: flowOf(emptyList())
    ) { state, localSongs, sharedSongs ->
        val userId = state.currentUserId

        // 1. Combine and deduplicate immediately
        // If we have the same ID in both lists, prefer the one that has an ownerId (more complete)
        val allSongs = (localSongs + sharedSongs)
            .filter { !it.isDeleted && !state.deletedSongIds.contains(it.id) } // CRITICAL: Filter out flagged or optimistically deleted songs
            .sortedByDescending { it.ownerId.isNotEmpty() }
            .distinctBy { it.id }
            
        // Diagnostic: Check for title duplicates with different IDs
        val titleMap = mutableMapOf<String, String>()
        allSongs.forEach { song ->
            if (titleMap.containsKey(song.title) && titleMap[song.title] != song.id) {
                println("DEBUG_HOME: Title duplication detected! Title: '${song.title}', IDs: [${titleMap[song.title]}, ${song.id}]")
            }
            titleMap[song.title] = song.id
        }

        if (allSongs.size > localSongs.size && sharedSongs.isEmpty()) {
             println("DEBUG_HOME: List size discrepancy. All: ${allSongs.size}, Local: ${localSongs.size}, Shared: ${sharedSongs.size}")
        }

        // 2. Filter by Tab
        val tabFiltered = if (userId == null) {
            // Security: When logged out, ONLY show anonymous songs
            allSongs.filter { it.ownerId.isEmpty() || it.ownerId == "anonymous" }
        } else {
            when (state.selectedTab) {
                SongTab.MY_SONGS -> allSongs.filter { 
                    it.ownerId == userId || it.ownerId.isEmpty() || it.ownerId == "anonymous"
                }
                SongTab.SHARED -> allSongs.filter { 
                    it.ownerId.isNotEmpty() && it.ownerId != userId 
                }
            }
        }

        // 3. Filter by Query
        val queryFiltered = if (state.searchQuery.isBlank()) {
            tabFiltered
        } else {
            tabFiltered.filter { song ->
                song.title.contains(state.searchQuery, ignoreCase = true) ||
                song.sections.any { section -> section.lines.any { line -> line.text.contains(state.searchQuery, ignoreCase = true) } }
            }
        }

        queryFiltered.sortedByDescending { it.lastModified }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    fun onTabSelected(tab: SongTab) {
        _uiState.update { it.copy(selectedTab = tab) }
        if (tab == SongTab.SHARED) {
            viewModelScope.launch { songRepository?.fetchInvitations() }
        }
    }

    fun onSearchQueryChange(query: String) {
        _uiState.update { it.copy(searchQuery = query) }
    }

    fun markNotificationsPrompted() {
        preferencesManager.hasPromptedForNotifications = true
        val userId = songRepository?.getCurrentUserId()
        if (userId != null) {
            viewModelScope.launch {
                songRepository.updateUserProfileFields(mapOf("hasPromptedForNotifications" to true))
            }
        }
    }

    fun createNewSong(onSongCreated: (String) -> Unit) {
        println("DEBUG_DIAG: createNewSong called")
        viewModelScope.launch {
            val userId = songRepository?.getCurrentUserId() ?: ""
            val newSong = Song.createEmpty().copy(id = uuid4().toString(), title = "New Song", ownerId = userId)
            println("DEBUG_DIAG: Saving new song ${newSong.id} to repository...")
            songRepository?.saveSong(newSong)
            println("DEBUG_DIAG: Song saved. Triggering onSongCreated callback...")
            onSongCreated(newSong.id)
            println("DEBUG_DIAG: onSongCreated returned.")
        }
    }

    fun loadSongs() {
        val userId = songRepository?.getCurrentUserId()
        
        // INSTANT UI: Only show the loading spinner if we have NO songs locally.
        // This ensures the "Empty State" (Create your first song) or existing songs show immediately.
        val hasLocalData = (songRepository?.songs?.value?.isNotEmpty() == true) || 
                           (songRepository?.sharedSongs?.value?.isNotEmpty() == true)
        
        _uiState.update { it.copy(isLoading = !hasLocalData, currentUserId = userId) }
        
        viewModelScope.launch {
             // 1. Trigger background sync
             if (userId != null) {
                 songRepository?.syncSongsWithFirestore {
                     // Callback fires as soon as cloud metadata (titles) is fetched
                     _uiState.update { it.copy(isLoading = false) }
                 }
             } else {
                 _uiState.update { it.copy(isLoading = false) }
             }
             
             // 2. First-time seeding (Local & Cloud)
             // We ONLY seed if the user has NEVER had a sample song seeded.
             // If they had it and deleted it, hasSeededSampleSongs will be true, and we won't re-seed.
             val currentProfile = songRepository?.currentUserProfile?.value
             val isSeededInCloud = currentProfile?.hasSeededSampleSongs == true
             val isSeededLocally = preferencesManager.hasSeededSampleSongs
             
             if (!isSeededLocally && !isSeededInCloud) {
                 val currentSongs = songRepository?.songs?.value ?: emptyList()
                 if (currentSongs.isEmpty()) {
                     println("DEBUG_HOME: Seeding sample song 'Wandering Soul'...")
                     preferencesManager.hasSeededSampleSongs = true
                     
                     // If logged in, also mark in cloud profile
                     if (userId != null) {
                         songRepository?.updateUserProfileFields(mapOf("hasSeededSampleSongs" to true))
                     }
                     
                     val sample = Song.createSample(userId).copy(ownerId = userId ?: "")
                     songRepository?.saveSong(sample)
                 } else {
                     // They already have songs (maybe from cloud), so mark as seeded to prevent future phantom seeds
                     preferencesManager.hasSeededSampleSongs = true
                     if (userId != null && !isSeededInCloud) {
                         songRepository?.updateUserProfileFields(mapOf("hasSeededSampleSongs" to true))
                     }
                 }
             } else if (isSeededInCloud && !isSeededLocally) {
                 // Sync cloud flag to local preference
                 preferencesManager.hasSeededSampleSongs = true
             }
        }
    }
    
    fun acceptInvitation(invitation: Invitation, currentTier: SubscriptionTier) {
        viewModelScope.launch { 
            try {
                // Check limits before accepting
                val currentSharedCount = songRepository?.sharedSongs?.value?.size ?: 0
                val limit = when (currentTier) {
                    SubscriptionTier.FREE -> 1
                    SubscriptionTier.HOBBYIST -> 5
                    SubscriptionTier.STUDIO -> Int.MAX_VALUE
                    SubscriptionTier.UNKNOWN -> 1
                }
                
                if (currentSharedCount >= limit) {
                    _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "You have reached your limit for shared songs. Upgrade to collaborate on more songs!") }
                    return@launch
                }

                _uiState.update { it.copy(isRespondingToInvitation = true) }
                val result = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                    songRepository?.respondToInvitation(invitation, true)
                }
                
                if (result != null) {
                    _uiState.update { it.copy(isRespondingToInvitation = false) }
                    // Rely on optimistic injection in SongRepository; calling syncSongsWithFirestore immediately causes a race condition with Firestore indexes.
                } else {
                    _uiState.update { it.copy(isRespondingToInvitation = false, error = "Request timed out. Please try again.") }
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                AppLogger.d("HomeViewModel", "[INVITE_DEBUG] Failed to accept invitation. Exception: ${e::class.simpleName}, Message: $msg")
                if (msg.contains("resource-exhausted") || msg.contains("limit")) {
                    _uiState.update { it.copy(isRespondingToInvitation = false, showUpgradeDialog = true, upgradeMessage = "You have reached your limit for shared songs. Upgrade to collaborate on more songs!") }
                } else {
                    _uiState.update { it.copy(isRespondingToInvitation = false, error = "Failed to accept invitation: $msg") }
                }
            }
        }
    }
    
    fun rejectInvitation(invitation: Invitation) {
        viewModelScope.launch { 
            try {
                _uiState.update { it.copy(isRespondingToInvitation = true) }
                val result = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                    songRepository?.respondToInvitation(invitation, false) 
                }
                if (result != null) {
                    _uiState.update { it.copy(isRespondingToInvitation = false) }
                } else {
                    _uiState.update { it.copy(isRespondingToInvitation = false, error = "Request timed out. Please try again.") }
                }
            } catch (e: Exception) {
                 _uiState.update { it.copy(isRespondingToInvitation = false, error = "Failed to reject invitation: ${e.message}") }
            }
        }
    }

    fun onSongDeleteRequest(song: Song) {
        _uiState.update { it.copy(songToDelete = song) }
    }

    fun confirmSongDeletion() {
        _uiState.value.songToDelete?.let { song ->
            val songId = song.id
            _uiState.update { it.copy(isLoading = true) }
            
            viewModelScope.launch {
                try {
                    val result = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                        songRepository?.deleteSong(songId)
                    }
                    
                    if (result != null || songRepository != null) { // withTimeoutOrNull returns Unit if successful
                        _uiState.update { it.copy(
                            isLoading = false,
                            songToDelete = null,
                            deletedSongIds = it.deletedSongIds + songId
                        ) }
                    } else {
                        _uiState.update { it.copy(
                            isLoading = false,
                            error = "Request timed out. Please try again."
                        ) }
                    }
                } catch (e: Exception) {
                    _uiState.update { it.copy(
                        isLoading = false,
                        error = "Failed to delete song: ${e.message}"
                    ) }
                }
            }
        }
    }

    fun cancelSongDeletion() {
        _uiState.update { it.copy(songToDelete = null) }
    }

    // --- Export Logic ---

    fun onSongToTextRequest(song: Song, subscriptionTier: SubscriptionTier) {
        if (subscriptionTier == SubscriptionTier.FREE) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Text and Audio Export are premium features. Please upgrade to unlock them.") }
        } else {
            // PRO SYNC: Ensure song is hydrated before showing export
            println("DEBUG_EXPORT: Export request for ${song.id}. Local section count: ${song.sections.size}")
            _uiState.update { it.copy(isSharingLoading = true) }
            viewModelScope.launch {
                try {
                    val fullSong = songRepository?.getSongById(song.id) ?: song
                    println("DEBUG_EXPORT: Hydration result for ${song.id}. Sections: ${fullSong.sections.size}, Lines: ${fullSong.sections.sumOf { it.lines.size }}")
                    _uiState.update { it.copy(songForTextExport = fullSong, isSharingLoading = false) }
                } catch (e: Exception) {
                    println("DEBUG_EXPORT: Hydration FAILED for ${song.id}: $e")
                    _uiState.update { it.copy(songForTextExport = song, isSharingLoading = false) }
                }
            }
        }
    }

    fun onSongToTextDismiss() {
        _uiState.update { it.copy(songForTextExport = null, songToTextState = SongToTextState()) }
    }

    fun onSongToTextOptionsChange(
        includeSections: Boolean? = null,
        includePiano: Boolean? = null,
        includeGuitar: Boolean? = null,
        includeChords: Boolean? = null,
        includeTabs: Boolean? = null,
        includeNotes: Boolean? = null
    ) {
        _uiState.update {
            it.copy(
                songToTextState = it.songToTextState.copy(
                    includeSections = includeSections ?: it.songToTextState.includeSections,
                    includePianoInfo = includePiano ?: it.songToTextState.includePianoInfo,
                    includeGuitarInfo = includeGuitar ?: it.songToTextState.includeGuitarInfo,
                    includeChordLabels = includeChords ?: it.songToTextState.includeChordLabels,
                    includeTabs = includeTabs ?: it.songToTextState.includeTabs,
                    includeNotes = includeNotes ?: it.songToTextState.includeNotes
                )
            )
        }
    }

    val generatedSongText: StateFlow<String> = _uiState.map { state ->
        val song = state.songForTextExport ?: return@map ""
        val options = state.songToTextState
        buildString {
            appendLine(song.title)
            appendLine("=".repeat(song.title.length))
            appendLine()
            if (options.includeGuitarInfo) {
                appendLine("Guitar Tuning: ${song.guitarTuning.joinToString(" ")}")
                if (song.guitarCapo > 0) appendLine("Capo: ${song.guitarCapo}")
                appendLine()
            }
            if (options.includePianoInfo || options.includeGuitarInfo) {
                appendLine("--- CHORD INFO ---")
                song.chordDefinitions.values.sortedBy { it.name }.forEach { chordDef ->
                    if (options.includePianoInfo && chordDef.pianoNotes != null) {
                        appendLine("  ${chordDef.name} (Piano): ${chordDef.pianoNotes.joinToString(", ")}")
                    }
                    if (options.includeGuitarInfo && chordDef.guitarTab != null) {
                        appendLine("  ${chordDef.name} (Guitar): ${chordDef.guitarTab.joinToString("-")}")
                    }
                }
                appendLine()
            }
            if (options.includeNotes && song.notes.isNotBlank()) {
                appendLine("--- NOTES ---")
                appendLine(song.notes)
                appendLine()
            }
            appendLine("--- LYRICS ---")
            val chordRegex = """\[.*?\]""".toRegex()
            song.sections.forEach { section ->
                if (options.includeSections) appendLine("[${section.title}]")
                if (options.includeTabs) section.tabs.forEach { appendLine(TabTextFormatter.formatTabToText(it)) }
                section.lines.forEach { line ->
                    appendLine(if (options.includeChordLabels) line.text else line.text.replace(chordRegex, ""))
                    if (options.includeTabs) line.tabs.forEach { appendLine(TabTextFormatter.formatTabToText(it)) }
                }
                appendLine()
            }
        }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), "")

    fun onAudioExportRequest(song: Song, subscriptionTier: SubscriptionTier) {
        if (subscriptionTier == SubscriptionTier.FREE) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Text and Audio Export are premium features. Please upgrade to unlock them.") }
        } else {
            _uiState.update { it.copy(songForAudioExport = song) }
        }
    }

    fun onAudioExportDismiss() {
        _uiState.update { it.copy(songForAudioExport = null) }
    }

    fun exportSong(song: Song, outputUri: String) {
        _uiState.update { it.copy(isExporting = true) }
        viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            try {
                val preparedSong = prepareSongForExport(song)
                audioExporter.exportSong(preparedSong, preparedSong.chordDefinitions, outputUri)
                _uiState.update { it.copy(isExporting = false, songForAudioExport = null) }
            } catch (e: Exception) {
                _uiState.update { it.copy(isExporting = false, error = e.message) }
            }
        }
    }

    private fun prepareSongForExport(originalSong: Song): Song {
        // 1. If Studio tracks exist, just resolve their URLs to local files if possible
        if (!originalSong.studioState?.tracks.isNullOrEmpty()) {
            val resolvedTracks = originalSong.studioState!!.tracks.map { track ->
                track.copy(items = track.items.map { item ->
                    if (item.type == "recording" && item.data.url != null) {
                        // Resolve URL to local file path using Repository
                        val recId = item.data.id
                        if (recId != null && songRepository != null) {
                            val resolvedPath = songRepository.getResolvedRecordingUrl(recId, item.data.url)
                            item.copy(data = item.data.copy(url = resolvedPath))
                        } else item
                    } else item
                })
            }
            return originalSong.copy(studioState = originalSong.studioState.copy(tracks = resolvedTracks))
        }

        // 2. If No Studio Tracks, synthesize one from the Line Recordings
        val syntheticItems = mutableListOf<TrackItem>()
        var currentBeat = 0f
        val beatsBuffer = 4f 
        val repo = songRepository ?: return originalSong // Cannot resolve paths without repo

        // Iterate sections/lines in order
        originalSong.sections.forEach { section ->
            // Check Section recordings
            val sectionRecs = originalSong.recordings[section.id]
            if (!sectionRecs.isNullOrEmpty()) {
                val bestTake = sectionRecs.last()
                val path = repo.getResolvedRecordingUrl(bestTake.id, bestTake.filePath)
                
                // Estimate duration or use a large one
                val estimatedDuration = 100f

                syntheticItems.add(TrackItem(
                    id = uuid4().toString(),
                    beat = currentBeat,
                    duration = estimatedDuration,
                    type = "recording",
                    data = TrackData(
                        name = "${section.title} (Audio)",
                        url = path,
                        id = bestTake.id
                    )
                ))
                currentBeat += estimatedDuration + beatsBuffer
            }

            // Check Line recordings
            section.lines.forEach { line ->
                val lineRecs = originalSong.recordings[line.id]
                if (!lineRecs.isNullOrEmpty()) {
                    val bestTake = lineRecs.last()
                    val path = repo.getResolvedRecordingUrl(bestTake.id, bestTake.filePath)

                    syntheticItems.add(
                        TrackItem(
                            id = uuid4().toString(),
                            beat = currentBeat,
                            duration = 100f,
                            type = "recording",
                            data = TrackData(
                                name = "Line Audio",
                                url = path,
                                id = bestTake.id
                            )
                        )
                    )
                    currentBeat += 100f + beatsBuffer
                }
            }
        }

        if (syntheticItems.isEmpty()) return originalSong // No recordings

        val syntheticTrack = Track(
            id = uuid4().toString(),
            name = "Vocal Recordings",
            items = syntheticItems
        )

        return originalSong.copy(
            studioState = StudioState(tracks = listOf(syntheticTrack))
        )
    }

    fun onUpgradeDialogDismiss() {
        _uiState.update { it.copy(showUpgradeDialog = false, upgradeMessage = null) }
    }

    // --- Share Logic ---
    fun onShareRequest(song: Song, subscriptionTier: SubscriptionTier) {
        val isOwner = song.ownerId == _uiState.value.currentUserId
        if (isOwner && subscriptionTier != SubscriptionTier.STUDIO) {
            _uiState.update { it.copy(showUpgradeDialog = true, upgradeMessage = "Sharing your own songs is a Studio feature. Please upgrade to invite collaborators!") }
        } else {
            _uiState.update { it.copy(shareDialogSong = song, shareError = null) }
        }
    }

    fun onShareDialogDismiss() {
        _uiState.update { it.copy(shareDialogSong = null, shareError = null) }
    }

    fun addCollaborator(email: String, isEditor: Boolean, currentTier: SubscriptionTier) {
        val currentSong = _uiState.value.shareDialogSong ?: return
        val normalizedInput = email.trim().lowercase()

        // 1. Check if user is blocked
        viewModelScope.launch {
            try {
                val currentProfile = songRepository?.currentUserProfile?.value ?: songRepository?.getCurrentUserProfile()
                val isBlocked = currentProfile?.blockedUsers?.any { 
                    it.value.lowercase() == normalizedInput || it.key == normalizedInput 
                } == true
                
                if (isBlocked) {
                    _uiState.update { it.copy(shareError = "You have blocked this user. Unblock them in Settings to collaborate.") }
                    return@launch
                }

                _uiState.update { it.copy(isSharingLoading = true, shareError = null) }
                
                // 2. Check Limits (Basic check on current user side)
                val collabCount = currentSong.collaborators.size + currentSong.viewers.size + currentSong.pendingCollaborators.size
                val limit = when (currentTier) {
                    SubscriptionTier.FREE -> CollaborationLimits.FREE_TIER_LIMIT
                    SubscriptionTier.HOBBYIST -> CollaborationLimits.HOBBYIST_TIER_LIMIT
                    SubscriptionTier.STUDIO -> Int.MAX_VALUE
                    else -> 0
                }
                
                if (collabCount >= limit) {
                     _uiState.update { it.copy(isSharingLoading = false, shareError = "Collaboration limit reached for your plan.") }
                     return@launch
                }
                
                val role = if (isEditor) "editor" else "viewer"
                val result = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                    songRepository?.sendInvitation(currentSong.id, email, role)
                }
                
                if (result != null || songRepository != null) {
                    // We don't get the updated song back immediately, but we can optimistically update
                    val updatedPending = currentSong.pendingCollaborators + (email to role)
                    val updatedSong = currentSong.copy(pendingCollaborators = updatedPending)
                    
                    _uiState.update { it.copy(isSharingLoading = false, shareDialogSong = updatedSong) }
                    // Refresh owner's UI
                    songRepository?.syncSongsWithFirestore {}
                    songwritersnotebook.app.utils.PlatformToast.show("Invitation sent.")
                } else {
                    _uiState.update { it.copy(isSharingLoading = false, shareError = "Request timed out. Please try again.") }
                }
            } catch (e: Exception) {
                val msg = e.message ?: "Failed to send invitation."
                _uiState.update { it.copy(isSharingLoading = false, shareError = if (msg.contains("limit")) "User has reached their collaboration limit." else msg) }
            }
        }
    }

    fun blockCollaborator(uid: String) {
        val currentSong = _uiState.value.shareDialogSong ?: return
        val displayName = currentSong.collaboratorEmails[uid] ?: "Unknown User"
        
        viewModelScope.launch {
            try {
                _uiState.update { it.copy(isSharingLoading = true) }
                
                // 1. Get current profile (prioritize cached, fallback to fetch)
                val currentProfile = songRepository?.currentUserProfile?.value ?: songRepository?.getCurrentUserProfile()
                if (currentProfile == null) {
                    songwritersnotebook.app.utils.PlatformToast.show("Error: Profile not found.")
                    return@launch
                }

                // 2. Add to blocked list in profile
                val updatedBlocked = currentProfile.blockedUsers.toMutableMap().apply {
                    put(uid, displayName)
                }
                songRepository?.updateUserProfileFields(mapOf("blockedUsers" to updatedBlocked))
                
                // 3. Remove from ALL owned songs
                songRepository?.removeAllCollaborationsForUser(uid)
                
                // 4. Update UI State optimistically for the current dialog song
                _uiState.update { state ->
                    val song = state.shareDialogSong
                    if (song != null) {
                        val updatedSong = song.copy(
                            collaborators = song.collaborators - uid,
                            viewers = song.viewers - uid,
                            collaboratorEmails = song.collaboratorEmails - uid
                        )
                        state.copy(shareDialogSong = updatedSong)
                    } else state
                }
                
                songwritersnotebook.app.utils.PlatformToast.show("User $displayName blocked.")
            } catch (e: Exception) {
                println("Failed to block user: ${e.message}")
                songwritersnotebook.app.utils.PlatformToast.show("Failed to block user.")
            } finally {
                _uiState.update { it.copy(isSharingLoading = false) }
            }
        }
    }

    fun reportCollaborator(uid: String) {
        val currentSong = _uiState.value.shareDialogSong ?: return
        val email = currentSong.collaboratorEmails[uid] ?: "Unknown"
        
        songwritersnotebook.app.utils.PlatformToast.show("Redirecting to email to report user...")
        
        // Simple reporting via email for now (compliance requirement)
        val platformUtils = songwritersnotebook.app.utils.PlatformUtils()
        platformUtils.sendEmail(
            to = "support@songwritersnotebook.app",
            subject = "UGC Report: User $uid",
            body = "Reporting User: $uid\nEmail: $email\nSong ID: ${currentSong.id}\nReason: [Please specify why you are reporting this user]"
        )
    }

    fun removeCollaborator(uidOrEmail: String) {
        val currentSong = _uiState.value.shareDialogSong ?: return
        
        _uiState.update { it.copy(isSharingLoading = true, shareError = null) }
        
        if (currentSong.pendingCollaborators.containsKey(uidOrEmail)) {
            viewModelScope.launch {
                try {
                    val result = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                        songRepository?.cancelInvitation(currentSong.id, uidOrEmail)
                    }
                    
                    if (result != null) {
                        val updatedPending = currentSong.pendingCollaborators.toMutableMap().apply { remove(uidOrEmail) }
                        val updatedSong = currentSong.copy(pendingCollaborators = updatedPending)
                        songRepository?.saveSongToAllSources(updatedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, isUndoable = false)
                        _uiState.update { it.copy(isSharingLoading = false, shareDialogSong = updatedSong) }
                        songRepository?.syncSongsWithFirestore {}
                    } else {
                        _uiState.update { it.copy(isSharingLoading = false, shareError = "Request timed out. Please try again.") }
                    }
                } catch (e: Exception) {
                    println("Failed to cancel invitation via function, trying removeCollaborator as fallback: $e")
                    // If it failed to cancel the invitation, it might be because the user ALREADY accepted it,
                    // but our local SQLite state hasn't synced yet to remove them from pending.
                    // Let's try removing them as a full collaborator.
                    try {
                        val potentialUid = currentSong.collaboratorEmails.entries.find { it.value.equals(uidOrEmail, ignoreCase = true) }?.key
                        if (potentialUid != null) {
                            val fbResult = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                                songRepository?.removeCollaborator(currentSong.id, potentialUid)
                            }
                            if (fbResult != null) {
                                val updatedSong = currentSong.copy(
                                    collaborators = currentSong.collaborators - potentialUid,
                                    viewers = currentSong.viewers - potentialUid,
                                    collaboratorEmails = currentSong.collaboratorEmails - potentialUid,
                                    pendingCollaborators = currentSong.pendingCollaborators.toMutableMap().apply { remove(uidOrEmail) }
                                )
                                _uiState.update { it.copy(isSharingLoading = false, shareDialogSong = updatedSong) }
                                songRepository?.syncSongsWithFirestore {}
                            } else {
                                _uiState.update { it.copy(isSharingLoading = false, shareError = "Request timed out. Please try again.") }
                            }
                        } else {
                            _uiState.update { it.copy(isSharingLoading = false, shareError = "Failed to remove invite: ${e.message}") }
                        }
                    } catch (inner: Exception) {
                        _uiState.update { it.copy(isSharingLoading = false, shareError = "Failed to remove invite or collaborator: ${inner.message}") }
                    }
                }
            }
            return
        }

        // Existing Collaborator (UID)
        viewModelScope.launch {
            try {
                val result = kotlinx.coroutines.withTimeoutOrNull(10000L) {
                    songRepository?.removeCollaborator(currentSong.id, uidOrEmail)
                }
                if (result != null) {
                    val updatedSong = currentSong.copy(
                        collaborators = currentSong.collaborators - uidOrEmail,
                        viewers = currentSong.viewers - uidOrEmail,
                        collaboratorEmails = currentSong.collaboratorEmails - uidOrEmail
                    )
                    _uiState.update { it.copy(isSharingLoading = false, shareDialogSong = updatedSong) }
                    songRepository?.syncSongsWithFirestore {}
                } else {
                    _uiState.update { it.copy(isSharingLoading = false, shareError = "Request timed out. Please try again.") }
                }
            } catch (e: Exception) {
                _uiState.update { it.copy(isSharingLoading = false, shareError = "Failed to remove collaborator: ${e.message}") }
            }
        }
    }
}

